/**
 * DimensionDetailModal.jsx — Deep dive on one trajectory dimension.
 *
 * Shows the score, why, improves, degrades and the 3 most profitable
 * actions to move this dimension up. Deterministically populated by the
 * backend `/api/trajectory/dimensions/{key}` endpoint.
 */
import { Link } from "react-router-dom";
import {
  TrendingUp, TrendingDown, Minus, ArrowRight, Sparkles, AlertTriangle,
} from "lucide-react";
import {
  Modal, Pill, NAVY, CHAMPAGNE, GOLD, SLATE, GREEN_LEAF, RED_LEAF, IVORY,
} from "@/components/altiora/PremiumUI";


export default function DimensionDetailModal({ dim, open, onClose }) {
  if (!dim) return null;
  const bandTone = dim.band === "strong" ? "green" : dim.band === "moderate" ? "amber" : "red";
  const bandColor = dim.band === "strong" ? GREEN_LEAF : dim.band === "moderate" ? CHAMPAGNE : RED_LEAF;
  const DeltaIcon = dim.delta_7d > 0 ? TrendingUp : dim.delta_7d < 0 ? TrendingDown : Minus;
  const deltaColor = dim.delta_7d > 0 ? "#065F46" : dim.delta_7d < 0 ? "#B91C1C" : SLATE;
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={dim.label}
      description={`Score : ${dim.score}/100`}
      testid={`dim-modal-${dim.key}`}
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 flex-wrap">
          <Pill tone={bandTone} testid={`dim-modal-band-${dim.key}`}>
            {dim.band === "strong" ? "Solide" : dim.band === "moderate" ? "À consolider" : "Fragile"}
          </Pill>
          <span className="inline-flex items-center gap-1 text-[12px]" style={{ color: deltaColor }}>
            <DeltaIcon className="h-3.5 w-3.5" strokeWidth={2} />
            {dim.delta_7d > 0 ? `+${dim.delta_7d}` : dim.delta_7d} pts sur 7 j
          </span>
          <span className="text-[10px] font-mono uppercase tracking-[0.22em]" style={{ color: SLATE }}>
            Poids : × {dim.weight}
          </span>
        </div>

        {/* Why */}
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
               style={{ color: CHAMPAGNE }}>
            Pourquoi ce score
          </div>
          <p className="text-[13px] leading-relaxed" style={{ color: NAVY }}
             data-testid={`dim-modal-why-${dim.key}`}>
            {dim.why}
          </p>
        </div>

        {/* Improves */}
        {dim.improves?.length > 0 && (
          <div className="rounded-md p-3"
               style={{ background: `${GREEN_LEAF}18`, border: `1px solid ${GREEN_LEAF}33` }}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                 style={{ color: "#065F46" }}>
              <Sparkles className="h-3 w-3" strokeWidth={2} />
              Ce qui améliore
            </div>
            <ul className="text-[13px] leading-relaxed space-y-1" style={{ color: NAVY }}>
              {dim.improves.map((s, i) => (
                <li key={i} data-testid={`dim-modal-improve-${dim.key}-${i}`}>• {s}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Degrades */}
        {dim.degrades?.length > 0 && (
          <div className="rounded-md p-3"
               style={{ background: `${RED_LEAF}22`, border: `1px solid ${RED_LEAF}55` }}>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                 style={{ color: "#7F1D1D" }}>
              <AlertTriangle className="h-3 w-3" strokeWidth={2} />
              Ce qui dégrade
            </div>
            <ul className="text-[13px] leading-relaxed space-y-1" style={{ color: NAVY }}>
              {dim.degrades.map((s, i) => (
                <li key={i} data-testid={`dim-modal-degrade-${dim.key}-${i}`}>• {s}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Top actions */}
        {dim.top_actions?.length > 0 && (
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] mb-2"
                 style={{ color: CHAMPAGNE }}>
              3 actions les plus rentables
            </div>
            <div className="space-y-2">
              {dim.top_actions.map((a, i) => (
                <ActionRow key={i} action={a} rank={i + 1} testid={`dim-modal-action-${dim.key}-${i}`} onClose={onClose} />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}


function ActionRow({ action, rank, testid, onClose }) {
  return (
    <Link
      to={action.cta_link || "#"}
      onClick={onClose}
      className="flex items-center justify-between gap-3 rounded-md p-3 hover:opacity-90 transition-opacity"
      style={{ background: `${CHAMPAGNE}12`, border: `1px solid ${CHAMPAGNE}33` }}
      data-testid={testid}
    >
      <div className="flex items-start gap-3 min-w-0">
        <div className="h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 font-serif text-sm"
             style={{ background: `${NAVY}0f`, color: NAVY }}>
          {rank}
        </div>
        <div className="min-w-0">
          <div className="text-[13px] font-medium truncate" style={{ color: NAVY }}>
            {action.title}
          </div>
          <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap" style={{ color: SLATE }}>
            <span>+{action.impact_pts} pts</span>
            <span>·</span>
            <span>{action.estimated_minutes} min</span>
            <span>·</span>
            <span>{action.source_module}</span>
          </div>
        </div>
      </div>
      <ArrowRight className="h-4 w-4 flex-shrink-0" style={{ color: CHAMPAGNE }} strokeWidth={2} />
    </Link>
  );
}
